import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clubs as mockClubs, events as mockEvents, news as mockNews } from '../data/mockData';
import { AdBox } from '../components/Shared';
import { useData } from '../context';
import api from '../api/client';

const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

function buildCalendar(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = (firstDay + 6) % 7;
  const cells = Array(offset).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

const MONTH_LT = ['Sausis','Vasaris','Kovas','Balandis','Gegužė','Birželis','Liepa','Rugpjūtis','Rugsėjis','Spalis','Lapkritis','Gruodis'];

const TYPE_BG = { club: 'bg-club', larp: 'bg-larp', ccg: 'bg-ccg', rpg: 'bg-rpg', fest: 'bg-fest' };
const EVENT_COLORS = { fest: '#ffa040', larp: '#ff8080', ccg: '#80b0ff', rpg: '#80d060' };
const NEWS_BG = {
  Naujienos: 'linear-gradient(135deg,#0e1a2c,#1a3c5c)',
  RPG:       'linear-gradient(135deg,#1a0f3c,#2d1a5c)',
  LARP:      'linear-gradient(135deg,#2c0e0e,#5c1a1a)',
  CCG:       'linear-gradient(135deg,#0e2c0e,#1a5c1a)',
};
const NEWS_BADGE = {
  Naujienos: 'rgba(26,60,92,0.9)',
  RPG:       'rgba(90,62,140,0.9)',
  LARP:      'rgba(92,26,26,0.9)',
  CCG:       'rgba(26,92,26,0.9)',
};
const NEWS_ICON = { Naujienos: '📰', RPG: '🎲', LARP: '⚔️', CCG: '🃏' };

const LARP_MONTHS = ['SAU','VAS','KOV','BAL','GEG','BIR','LIP','RUG','RGS','SPA','LAP','GRU'];
const SLIDES = 3;
const PER_SLIDE = 4;

function LarpSlideshow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [larpEvents, setLarpEvents] = useState([]);
  const [slide, setSlide] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get('/events', { params: { types: 'larp', limit: 12, page: 1 } })
      .then(r => {
        const d = r.data;
        setLarpEvents(Array.isArray(d) ? d : (d.events || []));
      }).catch(() => {});
  }, []);

  const startTimer = (idx) => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSlide(s => (s + 1) % SLIDES), 5000);
    setSlide(idx);
  };

  useEffect(() => {
    if (!larpEvents.length) return;
    timerRef.current = setInterval(() => setSlide(s => (s + 1) % SLIDES), 5000);
    return () => clearInterval(timerRef.current);
  }, [larpEvents.length]);

  const visible = larpEvents.slice(slide * PER_SLIDE, (slide + 1) * PER_SLIDE);
  if (!larpEvents.length) return null;

  return (
    <div style={{ background: 'linear-gradient(180deg,#0d0820,#050308)', borderTop: '1px solid rgba(255,128,128,0.18)', padding: '44px 24px 52px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 10, color: 'rgba(255,128,128,0.55)', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 5 }}>⚔️ LARP</div>
            <h2 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(18px,3vw,26px)', fontWeight: 700, color: '#ff8080', margin: 0, letterSpacing: 2, textTransform: 'uppercase', textShadow: '0 0 40px rgba(255,128,128,0.3)' }}>
              {t('larp_show.starts_soon')}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Dot indicators */}
            <div style={{ display: 'flex', gap: 7 }}>
              {Array.from({ length: SLIDES }).map((_, i) => (
                <button key={i} onClick={() => startTimer(i)}
                  style={{ width: slide === i ? 22 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer', background: slide === i ? '#ff8080' : 'rgba(255,128,128,0.22)', transition: 'all .3s', padding: 0 }} />
              ))}
            </div>
            <button onClick={() => startTimer((slide - 1 + SLIDES) % SLIDES)}
              style={{ background: 'rgba(255,128,128,0.1)', border: '1px solid rgba(255,128,128,0.3)', color: '#ff8080', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
            <button onClick={() => startTimer((slide + 1) % SLIDES)}
              style={{ background: 'rgba(255,128,128,0.1)', border: '1px solid rgba(255,128,128,0.3)', color: '#ff8080', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
            <button className="view-all" onClick={() => navigate('/larp')} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
              {t('larp_show.see_all')} →
            </button>
          </div>
        </div>

        {/* 4-card grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
          {visible.map(ev => {
            const parts = (ev.date || '').split('-');
            return (
              <div key={ev.id} onClick={() => navigate(`/events/${ev.id}`)}
                style={{ cursor: 'pointer', background: 'var(--bg)', border: '1px solid rgba(255,128,128,0.12)', borderRadius: 10, overflow: 'hidden', transition: 'all .2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,128,128,0.5)'; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(255,128,128,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,128,128,0.12)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ height: 150, backgroundColor: '#2c0e0e', backgroundImage: ev.image_url ? `url(${ev.image_url})` : 'linear-gradient(135deg,#2c0e0e,#5c1a1a)', backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44 }}>
                  {!ev.image_url && '⚔️'}
                  <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.024) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.024) 75%)', backgroundSize: '24px 24px' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(0,0,0,0.65) 0%,transparent 55%)', pointerEvents: 'none' }} />
                  {parts[2] && (
                    <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.72)', borderRadius: 7, padding: '5px 9px', textAlign: 'center', zIndex: 1, border: '1px solid rgba(255,128,128,0.25)' }}>
                      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 20, fontWeight: 700, color: '#ff8080', lineHeight: 1 }}>{parts[2]}</div>
                      <div style={{ fontSize: 9, color: 'rgba(255,200,200,0.65)', textTransform: 'uppercase', letterSpacing: 1 }}>{LARP_MONTHS[parseInt(parts[1]) - 1] || ''}</div>
                    </div>
                  )}
                </div>
                <div style={{ padding: '11px 13px' }}>
                  <div style={{ fontFamily: 'Cinzel,serif', fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 6, lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{ev.name}</div>
                  {(ev.city || ev.location) && (
                    <div style={{ fontSize: 11, color: 'rgba(200,150,150,0.65)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>📍 {ev.city || ev.location}</div>
                  )}
                  {ev.description && (
                    <div style={{ fontSize: 10, color: 'rgba(200,150,150,0.45)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{ev.description}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { clubs: apiClubs, events: apiEvents, news: apiNews, loading } = useData();
  const [counts, setCounts] = useState({ clubs: null, events: null, players: null });

  useEffect(() => {
    Promise.all([
      api.get('/clubs').then(r => r.data.total ?? r.data.length),
      api.get('/events').then(r => r.data.total ?? r.data.length),
      api.get('/players').then(r => r.data.total ?? r.data.length),
    ]).then(([c, e, p]) => setCounts({ clubs: c, events: e, players: p })).catch(() => {});
  }, []);

  const fmt = (n) => n == null ? '…' : n > 999 ? `${(n/1000).toFixed(1).replace('.0','')}k+` : `${n}+`;

  const clubs  = apiClubs.length  ? apiClubs  : mockClubs;
  const events = apiEvents.length ? apiEvents : mockEvents;
  const news   = apiNews.length   ? apiNews   : mockNews;

  const filtered = clubs.filter(c => {
    const matchFilter = filter === 'all' || c.type === filter || c.tags.some(tag => (tag || '').toLowerCase() === filter);
    const matchSearch = !search || (c.name || '').toLowerCase().includes(search.toLowerCase()) || (c.city || '').toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  // ── Dynamic calendar ──────────────────────────────────────────────────────
  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  const calDays = useMemo(() => buildCalendar(calYear, calMonth), [calYear, calMonth]);
  const today   = calYear === now.getFullYear() && calMonth === now.getMonth() ? now.getDate() : -1;

  const eventDayMap = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      if (!ev.date) return;
      const d = new Date(ev.date);
      if (d.getMonth() === calMonth) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(ev);
      }
    });
    return map;
  }, [events, calMonth]);

  const prevMonth = () => { if (calMonth === 0) { setCalYear(y=>y-1); setCalMonth(11); } else setCalMonth(m=>m-1); };
  const nextMonth = () => { if (calMonth === 11) { setCalYear(y=>y+1); setCalMonth(0); } else setCalMonth(m=>m+1); };

  // ── Skelbimai (8 atsitiktiniai, 2 eilės po 4) ─────────────────────────────
  const [marketItems, setMarketItems] = useState([]);
  useEffect(() => {
    api.get('/listings/random?count=8').then(r => setMarketItems(r.data || [])).catch(() => {});
  }, []);

  // ── Random players ────────────────────────────────────────────────────────
  const [sidePlayers, setSidePlayers] = useState([]);
  useEffect(() => {
    api.get('/players').then(r => {
      const arr = r.data || [];
      setSidePlayers([...arr].sort(() => Math.random() - 0.5).slice(0, 5));
    }).catch(() => {});
  }, []);

  // ── Featured clubs — random 3 ─────────────────────────────────────────────
  const featuredClubs = useMemo(() => {
    if (clubs.length <= 3) return clubs;
    const arr = [...clubs];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.slice(0, 3);
  }, [clubs]); // eslint-disable-line

  return (
    <>
      {/* ── WARHAMMER COMMUNITY STYLE HERO ── */}
      <div style={{ position: 'relative', background: 'linear-gradient(180deg,#050308 0%,#0d0820 40%,#110a2a 70%,#050308 100%)', padding: '72px 24px 60px', overflow: 'hidden', borderBottom: '1px solid rgba(240,192,64,0.2)' }}>
        {/* Grid background */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(240,192,64,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(240,192,64,0.04) 1px,transparent 1px)', backgroundSize: '80px 80px', pointerEvents: 'none' }} />
        {/* Radial glow */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 400, background: 'radial-gradient(ellipse,rgba(90,62,140,0.18) 0%,transparent 70%)', pointerEvents: 'none' }} />
        {/* Left / right fades */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,rgba(5,3,8,0.7) 0%,transparent 20%,transparent 80%,rgba(5,3,8,0.7) 100%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', maxWidth: 860, margin: '0 auto', textAlign: 'center' }}>
          {/* Eyebrow */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ height: 1, width: 40, background: 'linear-gradient(90deg,transparent,rgba(240,192,64,0.5))' }} />
            <span style={{ fontSize: 11, letterSpacing: 4, color: 'rgba(240,192,64,0.7)', textTransform: 'uppercase', fontFamily: 'Cinzel,serif' }}>Lietuvos Fantasy Bendruomenė</span>
            <div style={{ height: 1, width: 40, background: 'linear-gradient(90deg,rgba(240,192,64,0.5),transparent)' }} />
          </div>

          <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(32px,6vw,64px)', fontWeight: 700, color: '#f0c040', margin: '0 0 16px', lineHeight: 1.1, textShadow: '0 0 80px rgba(240,192,64,0.35),0 4px 8px rgba(0,0,0,0.9)', letterSpacing: 3, textTransform: 'uppercase' }}>
            {t('hero.title')}
          </h1>
          <p style={{ fontSize: 'clamp(13px,2vw,16px)', color: 'rgba(200,180,255,0.65)', marginBottom: 40, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 300 }}>
            {t('hero.subtitle')}
          </p>

          {/* Stats — WH Community bar style */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 0, marginBottom: 44, flexWrap: 'wrap' }}>
            {[[fmt(counts.clubs), t('hero.stat_clubs'), '🛡️'], [fmt(counts.events), t('hero.stat_events'), '⚔️'], [fmt(counts.players), t('hero.stat_members'), '👥']].map(([n, label, icon], i) => (
              <div key={label} style={{ padding: '0 32px', borderLeft: i > 0 ? '1px solid rgba(240,192,64,0.2)' : 'none', textAlign: 'center' }}>
                <div style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(24px,4vw,40px)', fontWeight: 700, color: '#f0c040', lineHeight: 1, textShadow: '0 0 20px rgba(240,192,64,0.4)' }}>{n}</div>
                <div style={{ fontSize: 10, color: 'rgba(200,180,255,0.5)', marginTop: 6, letterSpacing: 2, textTransform: 'uppercase' }}>{icon} {label}</div>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/events')}
              style={{ background: 'linear-gradient(135deg,#d4a017,#f0c040)', color: '#050308', border: 'none', borderRadius: 3, padding: '14px 36px', fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', boxShadow: '0 0 30px rgba(240,192,64,0.35)', transition: 'all .2s' }}
              onMouseEnter={e => e.currentTarget.style.transform='translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform='none'}
            >⚔️ {t('hero.join')}</button>
            <button
              onClick={() => navigate('/clubs')}
              style={{ background: 'transparent', color: '#f0c040', border: '1px solid rgba(240,192,64,0.4)', borderRadius: 3, padding: '14px 36px', fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(240,192,64,0.1)'; e.currentTarget.style.borderColor='rgba(240,192,64,0.7)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='rgba(240,192,64,0.4)'; }}
            >🗺️ {t('hero.explore')}</button>
          </div>
        </div>

        {/* Bottom ornament */}
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '80%', height: 1, background: 'linear-gradient(90deg,transparent,rgba(240,192,64,0.3),transparent)' }} />
      </div>

      {/* SEARCH BAR */}
      <div style={{ background: 'var(--bg2)', borderBottom: '1px solid var(--border)', padding: '12px 20px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}>🔍</span>
          <input
            className="form-input"
            style={{ paddingLeft: 34 }}
            placeholder={t('common.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {[['all', 'common.all'], ['club', 'nav.clubs'], ['larp', 'nav.larp'], ['ccg', 'CCG'], ['rpg', 'RPG']].map(([v, k]) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            style={{
              background: filter === v ? 'var(--purple)' : 'var(--bg)',
              border: `1px solid ${filter === v ? 'var(--purple)' : 'var(--border2)'}`,
              color: filter === v ? '#fff' : 'var(--text2)',
              padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', transition: 'all .2s', whiteSpace: 'nowrap',
            }}
          >
            {k.includes('.') ? t(k) : k}
          </button>
        ))}
      </div>

      {/* MAIN LAYOUT */}
      <div className="main-layout">

        {/* LEFT SIDEBAR — Artėjantys Renginiai */}
        <aside className="sidebar">
          <AdBox position="sidebar" />
          <div className="sidebar-section-title">📅 {t('events.upcoming')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {events.slice(0, 6).map(ev => (
              <div
                key={ev.id}
                onClick={() => navigate(`/events/${ev.id}`)}
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', transition: 'border-color .2s, transform .2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--purple2)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ background: `linear-gradient(135deg, ${EVENT_COLORS[ev.type] || '#5a3e8c'}22, ${EVENT_COLORS[ev.type] || '#5a3e8c'}44)`, borderBottom: `2px solid ${EVENT_COLORS[ev.type] || 'var(--purple)'}`, padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontFamily: 'Cinzel,serif', fontSize: 20, fontWeight: 700, color: EVENT_COLORS[ev.type] || 'var(--gold2)', lineHeight: 1 }}>
                      {ev.date.split('-')[2]}
                    </span>
                    <span style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>
                      {['SAU','VAS','KOV','BAL','GEG','BIR','LIP','RUG','RGS','SPA','LAP','GRU'][parseInt(ev.date.split('-')[1]) - 1]}
                    </span>
                  </div>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: (EVENT_COLORS[ev.type] || '#5a3e8c') + '33', color: EVENT_COLORS[ev.type] || 'var(--purple2)', border: `1px solid ${EVENT_COLORS[ev.type] || 'var(--purple)'}55` }}>
                    {ev.type.toUpperCase()}
                  </span>
                </div>
                <div style={{ padding: '7px 10px' }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 3, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>📍 {ev.city}</span>
                    <span style={{ color: '#ffd060' }}>⭐ {ev.rating}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button className="view-all" style={{ width: '100%', textAlign: 'center', padding: '8px 0', marginTop: 10 }} onClick={() => navigate('/events')}>
            {t('common.all_events')}
          </button>
        </aside>

        <main className="main-content">
          {/* CLUBS — 1×3 */}
          <div className="section-header">
            <h2 className="section-title" style={{ color: '#ffffff', fontFamily: 'Cinzel,serif', textShadow: '0 0 20px rgba(240,192,64,0.2)' }}>
              <span style={{ color: '#f0c040' }}>✦ </span>{t('clubs.featured')}
            </h2>
            <button className="view-all" onClick={() => navigate('/clubs')}>{t('common.view_all')}</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 28 }}>
            {featuredClubs.map(club => (
              <div key={club.id} className="card" onClick={() => navigate(`/clubs/${club.id}`)}>
                <div
                  className={`card-img ${TYPE_BG[(club.type || '').toLowerCase()] || 'bg-club'}`}
                  style={{
                    position: 'relative', overflow: 'hidden',
                    ...(club.image_url ? { backgroundImage: `url(${club.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                  }}
                >
                  {!club.image_url && <span style={{ fontSize: 40 }}>{club.emoji}</span>}
                  <span style={{ position: 'absolute', top: 8, left: 8, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: 'rgba(90,62,140,0.85)', color: '#fff', zIndex: 1 }}>
                    {(club.type || 'club').toUpperCase()}
                  </span>
                  <span style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', padding: '3px 7px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#ffd060', zIndex: 1 }}>
                    ⭐ {club.rating}
                  </span>
                  {club.status === 'pending' && (
                    <span style={{ position: 'absolute', bottom: 6, left: 8, fontSize: 10, background: '#ffa040', color: '#1a1a1a', padding: '2px 6px', borderRadius: 4, fontWeight: 700, zIndex: 1 }}>PENDING</span>
                  )}
                </div>
                <div className="card-body">
                  <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 4, lineHeight: 1.3 }}>{club.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>👥 {club.members} {t('clubs.members')}</span>
                    <span>📍 {club.city}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                    {club.tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* NEWS */}
          <div className="section-header">
            <h2 className="section-title" style={{ color: '#ffffff', fontFamily: 'Cinzel,serif', textShadow: '0 0 20px rgba(240,192,64,0.2)' }}>
              <span style={{ color: '#f0c040' }}>✦ </span>{t('news.title')}
            </h2>
            <button className="view-all" onClick={() => navigate('/news')}>{t('common.view_all')}</button>
          </div>

          {/* 2 didelės kortelės */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            {news.slice(0, 2).map(n => (
              <div key={n.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/news/${n.id}`)}>
                <div style={{ height: 160, background: NEWS_BG[n.category] || NEWS_BG.Naujienos, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', fontSize: 52, overflow: 'hidden' }}>
                  {n.image_url
                    ? <img src={n.image_url} alt={n.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    : NEWS_ICON[n.category] || '📰'
                  }
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(0,0,0,0.4) 0%,transparent 60%)', pointerEvents: 'none' }} />
                  <span style={{ position: 'absolute', top: 10, left: 10, fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 5, background: NEWS_BADGE[n.category] || NEWS_BADGE.Naujienos, color: '#fff', letterSpacing: 0.5, zIndex: 1 }}>
                    {n.category}
                  </span>
                </div>
                <div className="card-body">
                  <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 600, color: '#ffffff', marginBottom: 6, lineHeight: 1.4 }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>🕒 {n.date} &nbsp;·&nbsp; {n.author}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{stripHtml(n.content)}</div>
                </div>
              </div>
            ))}
          </div>

          {/* 4 mažos kortelės */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
            {news.slice(2, 6).map(n => (
              <div key={n.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/news/${n.id}`)}>
                <div style={{ height: 72, background: NEWS_BG[n.category] || NEWS_BG.Naujienos, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', fontSize: 28, overflow: 'hidden' }}>
                  {n.image_url
                    ? <img src={n.image_url} alt={n.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    : NEWS_ICON[n.category] || '📰'
                  }
                  <span style={{ position: 'absolute', top: 4, left: 5, fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: NEWS_BADGE[n.category] || NEWS_BADGE.Naujienos, color: '#fff', zIndex: 1 }}>
                    {n.category}
                  </span>
                </div>
                <div className="card-body" style={{ padding: '9px 10px' }}>
                  <div style={{ fontWeight: 500, fontSize: 12, color: '#ffffff', lineHeight: 1.4, marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>🕒 {n.date}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Kita puslapis */}
          <div style={{ textAlign: 'center', paddingBottom: 4 }}>
            <button className="btn btn-outline" onClick={() => navigate('/news')}>
              📰 {t('common.all_news')}
            </button>
          </div>

          {/* ── Random players ── */}
          {sidePlayers.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14, paddingBottom: 6, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                🧙 {t('clubs.sidebar_players')}
                <button className="view-all" style={{ marginLeft: 'auto', fontSize: 10 }} onClick={() => navigate('/players')}>{t('common.view_all')} →</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
                {sidePlayers.map(p => (
                  <div
                    key={p.user_id}
                    onClick={() => navigate(`/players/${p.user_id}`)}
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 10px', cursor: 'pointer', textAlign: 'center', transition: 'all .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(240,192,64,0.4)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                  >
                    <div style={{ width: 48, height: 48, borderRadius: '50%', margin: '0 auto 8px', overflow: 'hidden', background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(240,192,64,0.25)' }}>
                      {p.avatar
                        ? <img src={p.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: '#f0c040', fontWeight: 700 }}>{(p.name || '?')[0].toUpperCase()}</span>
                      }
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || 'Žaidėjas'}</div>
                    {p.city && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>📍 {p.city}</div>}
                    {p.experience && <div style={{ fontSize: 9, marginTop: 4, padding: '1px 6px', borderRadius: 4, background: 'rgba(90,62,140,0.2)', border: '1px solid rgba(90,62,140,0.35)', color: '#c8b4ff', display: 'inline-block' }}>{p.experience}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Skelbimų karuselė (turgus) ── */}
          {marketItems.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14, paddingBottom: 6, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                🛒 {t('nav.listings')}
                <button className="view-all" style={{ marginLeft: 'auto', fontSize: 10 }} onClick={() => navigate('/skelbimai')}>{t('common.view_all')} →</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {marketItems.slice(0, 8).map(l => {
                  const img = l.images?.[0];
                  const priceTxt = l.listing_type === 'exchange' && !Number(l.price)
                    ? '🔄 Mainai'
                    : `${l.listing_type === 'auction' ? '🔨 ' : ''}${(Number(l.listing_type === 'auction' ? (l.highest_bid ?? l.price) : l.price) || 0)} ${{ EUR: '€', USD: '$', GBP: '£' }[l.currency] || l.currency}`;
                  return (
                    <div
                      key={l.id}
                      onClick={() => navigate(`/skelbimai/${l.id}`)}
                      style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', transition: 'all .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(240,192,64,0.4)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                    >
                      <div style={{ aspectRatio: '4/3', background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {img
                          ? <img src={img} alt={l.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: 30 }}>🎲</span>}
                      </div>
                      <div style={{ padding: '8px 10px' }}>
                        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.35, marginBottom: 4, minHeight: 31 }}>
                          {l.title}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                          <span style={{ fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 800, color: '#f0c040' }}>{priceTxt}</span>
                          {l.city && <span style={{ fontSize: 9.5, color: 'var(--text3)' }}>📍 {l.city}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </main>

        {/* ASIDE */}
        <aside className="aside-panel">

          {/* Mini calendar */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 11, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
              📅 {t('common.calendar')}
            </div>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg,var(--purple),var(--purple2))', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 16 }} onClick={prevMonth}>‹</button>
                <span style={{ fontFamily: 'Cinzel,serif', fontSize: 13, color: '#fff', fontWeight: 600 }}>{MONTH_LT[calMonth]} {calYear}</span>
                <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 16 }} onClick={nextMonth}>›</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1, background: 'var(--border)', padding: 1 }}>
                {['P','A','T','K','Pn','Š','S'].map(d => (
                  <div key={d} style={{ background: 'var(--bg2)', textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text3)', padding: '5px 0' }}>{d}</div>
                ))}
                {calDays.map((day, i) => {
                  const hasEvents = day && eventDayMap[day];
                  const isToday   = day === today;
                  const dateStr   = day ? `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : '';
                  return (
                    <div
                      key={i}
                      title={hasEvents ? `${eventDayMap[day].length} renginys(-iai)` : undefined}
                      onClick={() => hasEvents && navigate(`/events?date=${dateStr}`)}
                      style={{
                        background: isToday ? 'rgba(240,192,64,0.1)' : 'var(--bg)',
                        textAlign: 'center', padding: '5px 2px', fontSize: 11,
                        cursor: hasEvents ? 'pointer' : day ? 'default' : 'default',
                        position: 'relative', minHeight: 26,
                        color: !day ? 'transparent' : isToday ? 'var(--gold2)' : 'var(--text)',
                        fontWeight: isToday ? 700 : 400,
                        transition: hasEvents ? 'background .15s' : undefined,
                      }}
                      onMouseEnter={e => { if (hasEvents) e.currentTarget.style.background = 'rgba(240,192,64,0.15)'; }}
                      onMouseLeave={e => { if (hasEvents) e.currentTarget.style.background = isToday ? 'rgba(240,192,64,0.1)' : 'var(--bg)'; }}
                    >
                      {day}
                      {hasEvents && (
                        <span style={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: 'var(--gold2)', display: 'block' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Išskirtiniai Klubai — vertikaliai */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 11, fontWeight: 600, color: 'var(--gold2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
              🏆 {t('clubs.featured')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {featuredClubs.map(club => (
                <div
                  key={club.id}
                  onClick={() => navigate(`/clubs/${club.id}`)}
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', transition: 'border-color .2s, transform .2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--purple2)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <div
                    className={`card-img ${TYPE_BG[(club.type || '').toLowerCase()] || 'bg-club'}`}
                    style={{
                      position: 'relative', height: 60, fontSize: 26, overflow: 'hidden',
                      ...(club.image_url ? { backgroundImage: `url(${club.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                    }}
                  >
                    {!club.image_url && club.emoji}
                    <span style={{ position: 'absolute', top: 5, left: 6, fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(90,62,140,0.85)', color: '#fff', zIndex: 1 }}>
                      {(club.type || 'club').toUpperCase()}
                    </span>
                    <span style={{ position: 'absolute', top: 5, right: 6, background: 'rgba(0,0,0,0.55)', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#ffd060', zIndex: 1 }}>
                      ⭐ {club.rating}
                    </span>
                  </div>
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ fontFamily: 'Cinzel,serif', fontSize: 12, fontWeight: 600, color: '#ffffff', marginBottom: 2, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {club.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 8 }}>
                      <span>👥 {club.members}</span><span>📍 {club.city}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="view-all" style={{ width: '100%', textAlign: 'center', padding: '7px 0', marginTop: 8 }} onClick={() => navigate('/clubs')}>
              {t('common.all_clubs')}
            </button>
          </div>

          <AdBox position="sidebar" />
        </aside>
      </div>

      <LarpSlideshow />
    </>
  );
}
